import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { CORE_MODEL_INFO } from '../../../utils/coreModel';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/**
 * PlacementStep - Slide 2.4: Find Good Location
 *
 * Shows model-specific placement guidance with:
 * - Dynamic heading with device model
 * - Model-specific body text (Elite has different text)
 * - Model-specific placement image (from CORE_MODEL_INFO)
 * - Chatbot button with model-dependent flowId (from CORE_MODEL_INFO)
 */
export const PlacementStep: React.FC = () => {
  const { deviceModel, goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_chooseLocation');
  }, [setMainStepId]);

  const handleContinue = () => {
    goToStep('connect-overview');
  };

  // Get placement image from CORE_MODEL_INFO, fallback to Pro if unknown
  const placementImage = deviceModel
    ? CORE_MODEL_INFO[deviceModel].placementImage
    : CORE_MODEL_INFO.Pro.placementImage;

  // Get placement text based on model
  const placementText =
    deviceModel === 'Elite_AI'
      ? __(
          'Choose a flat, stable surface or rack-mount location near your router and power. Leave space around the Core for ventilation. Ensure proper airflow in rack environments.'
        )
      : __(
          'Choose a flat, stable surface near your router and power. Leave space around the Core for ventilation. Avoid placing it on the floor or in an enclosed cabinet.'
        );

  // Get model-dependent chatbot flowId from CORE_MODEL_INFO
  const placementFlowId = deviceModel
    ? CORE_MODEL_INFO[deviceModel].placementFlowId
    : CORE_MODEL_INFO.Pro.placementFlowId;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Place your Core $[model].', { model: deviceModel ? CORE_MODEL_INFO[deviceModel].short : '' })}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        {placementText}
      </Typography>

      {/* Placement image */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
        }}
      >
        <Box
          component="img"
          src={placementImage}
          alt={__('Core placement guidance')}
          sx={{
            maxWidth: '100%',
            maxHeight: 280,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Guidance section with chatbot button */}
      <GuidanceSection
        buttons={[
          {
            label: __('I have questions about where to place my Core'),
            flowId: placementFlowId,
          },
        ]}
      />

      <WizardNavigation
        primaryLabel={__('Continue')}
        onPrimary={handleContinue}
      />
    </Box>
  );
};
